import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import toast from 'react-hot-toast';
import SEO from '../components/SEO';

const DeleteUserData = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    reason: '',
    confirmEmail: '',
    agreeTerms: false
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.reason.trim()) {
      toast.error('Please provide a reason for deletion');
      return;
    }

    if (formData.confirmEmail !== currentUser?.email) {
      toast.error('Email does not match your account email');
      return;
    }

    if (!formData.agreeTerms) {
      toast.error('Please confirm that you understand the consequences');
      return;
    }

    setIsSubmitting(true);
    try {
      // Create deletion request in Firestore
      await addDoc(collection(db, 'deleteRequests'), {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userName: userProfile?.fullName || 'N/A',
        userPhone: userProfile?.phone || 'N/A',
        reason: formData.reason,
        requestedAt: new Date(),
        status: 'pending'
      });

      toast.success('Deletion request submitted successfully!');
      setTimeout(() => {
        navigate('/profile');
      }, 2000);
    } catch (error) {
      console.error('Error submitting deletion request:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Request Data Deletion | House of Deepak"
        description="Request deletion of your personal data from House of Deepak"
      />
      <div className="min-h-screen bg-cream pt-20 pb-16">
        <div className="section-padding">
          <div className="container-max max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {/* Warning Banner */}
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-red-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Warning: Data Deletion Request
                    </h3>
                    <p className="mt-2 text-sm text-red-700">
                      Submitting this request will initiate the deletion process of your
                      personal data. This action cannot be undone once approved by our team.
                    </p>
                  </div>
                </div>
              </div>

              {/* Header */}
              <div className="text-center mb-8">
                <h1 className="text-3xl md:text-4xl font-serif text-deep-brown mb-4">
                  Request Data Deletion
                </h1>
                <p className="text-warm-gray">
                  We'll review your request and process it within 48-72 hours
                </p>
              </div>

              {/* Info Card */}
              <div className="bg-white rounded-lg shadow-sm border border-warm-gray/20 p-6 mb-6">
                <h2 className="text-xl font-serif text-deep-brown mb-4">
                  What Will Be Deleted?
                </h2>
                <ul className="space-y-2 text-sm text-warm-gray">
                  <li className="flex items-start">
                    <span className="text-sage mr-2">✓</span>
                    <span>Your account and login credentials</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-sage mr-2">✓</span>
                    <span>Personal information (name, email, phone, address)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-sage mr-2">✓</span>
                    <span>Shopping cart and saved items</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-500 mr-2">⚠</span>
                    <span>
                      Order history will be anonymized (kept for business records)
                    </span>
                  </li>
                </ul>
              </div>

              {/* Form */}
              <div className="bg-white rounded-lg shadow-sm border border-warm-gray/20 p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Current Account Info */}
                  <div className="bg-cream/50 rounded-lg p-4">
                    <p className="text-sm text-warm-gray mb-2">
                      <strong className="text-deep-brown">Account:</strong>{' '}
                      {currentUser?.email}
                    </p>
                    <p className="text-sm text-warm-gray">
                      <strong className="text-deep-brown">Name:</strong>{' '}
                      {userProfile?.fullName || 'N/A'}
                    </p>
                  </div>

                  {/* Reason */}
                  <div>
                    <label className="block text-sm font-medium text-deep-brown mb-2">
                      Reason for Deletion *
                    </label>
                    <textarea
                      name="reason"
                      value={formData.reason}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-3 py-2 border border-warm-gray/30 rounded-md focus:outline-none focus:ring-gold focus:border-gold resize-none"
                      placeholder="Please tell us why you want to delete your account (optional but helpful for us to improve)"
                      required
                    />
                  </div>

                  {/* Confirm Email */}
                  <div>
                    <label className="block text-sm font-medium text-deep-brown mb-2">
                      Confirm Your Email *
                    </label>
                    <input
                      type="email"
                      name="confirmEmail"
                      value={formData.confirmEmail}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-warm-gray/30 rounded-md focus:outline-none focus:ring-gold focus:border-gold"
                      placeholder="Enter your email to confirm"
                      required
                    />
                    <p className="mt-1 text-xs text-warm-gray">
                      Type <strong>{currentUser?.email}</strong> to confirm
                    </p>
                  </div>

                  {/* Confirmation Checkbox */}
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      name="agreeTerms"
                      checked={formData.agreeTerms}
                      onChange={handleInputChange}
                      className="mt-1 w-4 h-4 text-red-600 border-warm-gray/30 rounded focus:ring-red-500"
                      required
                    />
                    <label className="ml-3 text-sm text-warm-gray">
                      I understand that this action is permanent and cannot be undone. All my
                      personal data will be deleted after admin approval.
                    </label>
                  </div>

                  {/* Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4 pt-4">
                    <button
                      type="button"
                      onClick={() => navigate('/profile')}
                      className="flex-1 px-6 py-3 border border-warm-gray/30 text-deep-brown rounded-md hover:bg-gray-50 transition-colors duration-300"
                    >
                      Cancel
                    </button>
                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 bg-red-500 text-white px-6 py-3 rounded-md font-medium hover:bg-red-600 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                      whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Deletion Request'}
                    </motion.button>
                  </div>
                </form>
              </div>

              {/* Additional Info */}
              <div className="mt-6 text-center text-sm text-warm-gray">
                <p>
                  Have questions?{' '}
                  <a
                    href="/contact"
                    className="text-deep-brown hover:text-gold transition-colors duration-200 underline"
                  >
                    Contact our support team
                  </a>
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DeleteUserData;
